import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import {
  UnauthenticatedError,
  useOnboardingRequirements,
} from '@/hooks/useOnboardingRequirements'
import { authService } from '@/services/auth'
import { hasPendingOnboarding } from '@/utils/signupResume'

type OnboardingGateState = {
  isAuthenticated: boolean
  needsOnboarding: boolean
  refreshAuth: () => void
}

const OnboardingGateContext = createContext<OnboardingGateState | null>(null)

export const useOnboardingGate = (): OnboardingGateState => {
  const ctx = useContext(OnboardingGateContext)
  if (!ctx) {
    throw new Error('useOnboardingGate must be used within OnboardingGate')
  }
  return ctx
}

const Shell = ({ children }: { children: ReactNode }) => (
  <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden relative max-h-screen">
    <div className="h-[600px] flex items-center justify-center px-6">
      {children}
    </div>
  </div>
)

/**
 * Session + onboarding gate (same role as frontend-rework's OnboardingGate).
 * Unauthenticated users pass through immediately; authenticated users wait on
 * requirements before children render.
 */
export const OnboardingGate = ({ children }: { children: ReactNode }) => {
  const [hasToken, setHasToken] = useState(() => authService.isAuthenticated())

  const {
    data: requirements,
    isLoading,
    isError,
    error,
    refetch,
  } = useOnboardingRequirements(hasToken)

  const refreshAuth = useCallback(() => {
    const isAuth = authService.isAuthenticated()
    setHasToken(isAuth)
    if (isAuth) {
      void refetch()
    }
  }, [refetch])

  useEffect(() => {
    if (!hasToken) return
    if (
      !authService.isAuthenticated() ||
      error instanceof UnauthenticatedError
    ) {
      setHasToken(false)
    }
  }, [hasToken, error, isError])

  if (!hasToken) {
    return (
      <OnboardingGateContext.Provider
        value={{
          isAuthenticated: false,
          needsOnboarding: false,
          refreshAuth,
        }}
      >
        {children}
      </OnboardingGateContext.Provider>
    )
  }

  if (isLoading) {
    return (
      <Shell>
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Checking authentication...
          </p>
        </div>
      </Shell>
    )
  }

  if (isError || !requirements) {
    return (
      <Shell>
        <div className="text-center space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Unable to verify your account status. Please try again.
          </p>
          <button
            type="button"
            onClick={() => {
              if (!authService.isAuthenticated()) {
                setHasToken(false)
                return
              }
              void refetch()
            }}
            className="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
          >
            Try again
          </button>
        </div>
      </Shell>
    )
  }

  return (
    <OnboardingGateContext.Provider
      value={{
        isAuthenticated: true,
        needsOnboarding: hasPendingOnboarding(requirements),
        refreshAuth,
      }}
    >
      {children}
    </OnboardingGateContext.Provider>
  )
}
